import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { useDialog } from "@datanac/datanac-mui-toolkit";
import { Alert, Box, Divider, FormControl, Grid, Typography } from "@mui/material";
import { UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { MASK_NUMERIC, StatefulControlledTextField } from "components/Core/StatefulControlledTextField";
import { FinancialIcon } from "components/Icons/Icons";
import { MODULE_FINANCIAL } from "components/Menu/config/constants";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import _ from "lodash";
import { useFormStatus } from "mui-toolkit/useFormStatus";
import { useSnackbar } from "notistack";
import { v4 as uuidv4 } from "uuid";

import { useChatAssistantAttachments } from "../../../contexts/useChatAssistantAttachments";
import useFinanceReport from "../useFinanceReport";

export default function ScheduleFWorkspace() {
  const { globalState, globalDispatch } = useContext(AppContext);

  const [queryString] = useSearchParams();

  const { enqueueSnackbar } = useSnackbar();

  const { component: confirmDialogComponent } = useDialog();
  const { component: formStatusComponent, actions: formStatusActions } = useFormStatus();

  const [currentYear, setCurrentYear] = useState(queryString.get("year") || globalState.crop_year);
  const { addAttachments } = useChatAssistantAttachments();
  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const {
    isLoading,

    scheduleF,
    loadScheduleF,
  } = useFinanceReport({
    onError: handleError,
    currentYear,

    loadModules: [MODULE_FINANCIAL],
  });

  useEffect(() => {
    if (currentYear != queryString.get("year")) {
      globalDispatch({
        type: "update",
        payload: {
          key: "crop_year",
          value: currentYear,
        },
      });
    }
  }, [currentYear]);

  useEffect(() => {
    addAttachments([{ name: "Schedule F", content: scheduleF }]);
  }, [scheduleF]);

  const calculateContainersIncome = () => {
    return _.uniq(scheduleF?.income?.values?.map((v) => v.container));
  };

  const calculateContainersExpenses = () => {
    return _.uniq(scheduleF?.expenses?.values?.map((v) => v.container));
  };

  // --- --- ---

  const handleChangeState_LineItem = async (event, _currentDocumentDictionary) => {
    if (event?.target?.value != scheduleF?.values.find((v) => v.id == _currentDocumentDictionary?.id)?.value) {
      formStatusActions.setIsFormDirty(true, true);

      const _newValue = {
        ..._currentDocumentDictionary,
        id: _currentDocumentDictionary?.id || uuidv4(),
        value: event?.target?.value || 0,
      };

      const _uploadFormData = {
        producer_token: globalState.producer_token,
        agent_token: globalState.agent_token,
        Document: {
          id: _newValue?.document_id || uuidv4(),
          values: [_newValue],
        },
      };

      await UsersApiHelper.updateFinancialDocument(scheduleF, globalState);

      UsersApiHelper.updateDocumentDictionary(_uploadFormData, globalState)
        .then(() => {
          formStatusActions.setIsSaving(false);
          loadScheduleF();
        })
        .catch((err) => {
          formStatusActions.setIsFormDirty(true, false);
          handleError(err);
        });
    }
  };

  const formatName = (value) => {
    if (value) {
      return value.replaceAll("_", " ");
    }
  };

  // --- --- ---

  return (
    <>
      <WorkspaceHeader
        title="Schedule F"
        subtitle="Form 1040"
        category={{
          title: "Financial Analysis",
          icon: <FinancialIcon />,
        }}
      />

      <WorkspacePageHeader
        title="Year"
        currentYear={currentYear}
        onYearChange={setCurrentYear}
        isLoading={isLoading}
        additionalControls={formStatusComponent}
      />

      {scheduleF?.values?.length === 0 && (
        <>
          <Alert severity="warning">
            No Schedule F form is found.
            <Divider />
            Please upload or enter the document and try again.
          </Alert>
        </>
      )}

      {Boolean(scheduleF?.values?.length) && (
        <>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Part I - Farm Income
          </Typography>
          {calculateContainersIncome()?.map((container) => (
            <Box key={container} sx={{ mt: 2, mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, textTransform: "capitalize" }}>
                {formatName(container)}
              </Typography>

              <Grid container spacing={2}>
                {Boolean(scheduleF?.values?.length) &&
                  scheduleF?.values
                    ?.filter((v) => v.container == container)
                    .map((v, index) => (
                      <Grid item key={index} xs={6}>
                        <FormControl fullWidth={true}>
                          <StatefulControlledTextField
                            label={formatName(v.item)}
                            name={v.item}
                            isRequired={false}
                            defaultValue={v.value}
                            variant="outlined"
                            onChange={(e) => handleChangeState_LineItem(e, v)}
                            InputLabelProps={{ shrink: true, sx: { textTransform: "capitalize" } }}
                            maskType={MASK_NUMERIC}
                          />
                        </FormControl>

                        <br />
                      </Grid>
                    ))}
              </Grid>
            </Box>
          ))}

          <Typography variant="h6" sx={{ mb: 2 }}>
            Part II - Farm Expenses
          </Typography>
          {calculateContainersExpenses()?.map((container) => (
            <Box key={container} sx={{ mt: 2, mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, textTransform: "capitalize" }}>
                {formatName(container)}
              </Typography>

              <Grid container spacing={2}>
                {Boolean(scheduleF?.values?.length) &&
                  scheduleF?.values
                    ?.filter((v) => v.container == container)
                    .map((v, index) => (
                      <Grid item key={index} xs={6}>
                        <FormControl fullWidth={true}>
                          <StatefulControlledTextField
                            label={formatName(v.item)}
                            name={v.item}
                            isRequired={false}
                            defaultValue={v.value}
                            variant="outlined"
                            onChange={(e) => handleChangeState_LineItem(e, v)}
                            InputLabelProps={{ shrink: true, sx: { textTransform: "capitalize" } }}
                            maskType={MASK_NUMERIC}
                          />
                        </FormControl>

                        <br />
                      </Grid>
                    ))}
              </Grid>
            </Box>
          ))}
        </>
      )}

      <WorkspaceActionBar content={<></>} />

      {confirmDialogComponent}
    </>
  );
}
